import { useToast } from "@app/core";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

import useVendorFilters from "@/shared/api/useVendorFilters";
import { useGlobalFiltersStore, type BrandFilterOption } from "@/shared/stores/useGlobalFiltersStore";
import type { VendorBrand, VendorBrandGroup, VendorProduct } from "@/shared/types/partnership";

interface VendorProductSearchResult extends VendorProduct {
  label_title: string
  label_name: string
}

const ALL_VENDOR_BRANDS_OPTION: BrandFilterOption = { label: "All", value: "all" };

// Backend requires an explicit limit for the product search
const VENDOR_PRODUCT_SEARCH_LIMIT = 100;

/**
 * Vendor Central analog of the seller brands/products slice in useGlobalFiltersStore.
 * Kept as a separate store on purpose: seller and vendor selections live in parallel,
 * so switching partnership never loses either state. Dates/channels/granularity stay
 * shared in useGlobalFiltersStore.
 */
export const useVendorFiltersStore = defineStore("vendorFilters", () => {
  const {
    fetchVendorBrands: fetchVendorBrandsRequest,
    fetchVendorBrandGroups: fetchVendorBrandGroupsRequest,
    searchVendorProducts: searchVendorProductsRequest,
  } = useVendorFilters();
  const { warning } = useToast();

  // Dates/channels/granularity are shared filters and live in the global store.
  // Safe one-way dependency: the global store never imports this one.
  const globalFilters = useGlobalFiltersStore();

  // ==================== State ====================

  /**
   * Flag indicating vendor filters are initialized (list loaded + URL restored).
   * Set on the first entry into vendor mode.
   */
  const isInitialized = ref(false);

  /**
   * Vendor brands list
   */
  const vendorBrands = ref<VendorBrand[]>([]);
  const isVendorBrandLoading = ref(false);

  /**
   * Vendor brand groups list
   */
  const vendorBrandGroups = ref<VendorBrandGroup[]>([]);
  const isVendorBrandGroupLoading = ref(false);

  /**
   * Selected vendor brands (for filter)
   * Contains "All" option or specific brand options
   */
  const selectedVendorBrands = ref<BrandFilterOption[]>([ALL_VENDOR_BRANDS_OPTION]);

  /**
   * Vendor product search filter (ASINs)
   */
  const vendorProductSearch = ref<string[]>([]);

  /**
   * Vendor product search results
   */
  const searchVendorProductResult = ref<VendorProductSearchResult[]>([]);
  const isSearchVendorProductLoading = ref(false);

  // ==================== Getters ====================

  /**
   * Check if all vendor brands selected
   */
  const isAllVendorBrandsSelected = computed(() => {
    return selectedVendorBrands.value.some(b => b.value === "all");
  });

  /**
   * Get selected vendor brand short names for API calls
   * Returns empty array if "All" is selected (meaning no filter)
   */
  const selectedVendorBrandIds = computed<string[]>(() => {
    if (isAllVendorBrandsSelected.value) return [];
    return selectedVendorBrands.value
      .map(b => b.shortName)
      .filter((s): s is string => !!s);
  });

  /**
   * Get selected vendor product IDs for API calls
   */
  const selectedVendorProductIds = computed<number[]>(() => {
    return searchVendorProductResult.value.map(p => p.VENDOR_PRODUCT_PK);
  });

  // ==================== API Request Params (ready-to-use objects) ====================
  // Mirror of kpiRequestParams / tableRequestParams in useGlobalFiltersStore:
  // vendor brand/product selection + shared dates/channels/granularity.
  // Key names mirror the seller contract — adjust when vendor endpoint contracts land.

  /**
   * Watch-source for useTablePage in vendor tables
   *
   * @example
   * const page = useTablePage([() => vendorFiltersStore.tableFilters]);
   */
  const tableFilters = computed(() => [
    globalFilters.formattedPrimaryInterval,
    selectedVendorBrandIds.value,
    globalFilters.selectedChannelIds,
    selectedVendorProductIds.value,
    globalFilters.currentGranularity,
  ]);

  /**
   * KPI request params - for vendor KPI blocks that need comparison data
   * Ready to spread into API calls: { ...vendorFiltersStore.kpiRequestParams }
   */
  const kpiRequestParams = computed(() => ({
    brand: selectedVendorBrandIds.value,
    channel: globalFilters.selectedChannelIds,
    product: selectedVendorProductIds.value,
    primaryInterval: globalFilters.formattedPrimaryInterval,
    secondaryInterval: globalFilters.formattedSecondaryInterval,
  }));

  /**
   * Table/Chart request params - for vendor blocks that don't need comparison
   * Ready to spread into API calls: { ...vendorFiltersStore.tableRequestParams }
   */
  const tableRequestParams = computed(() => ({
    brand: selectedVendorBrandIds.value,
    channel: globalFilters.selectedChannelIds,
    product: selectedVendorProductIds.value,
    granularity: globalFilters.currentGranularity,
    period: globalFilters.formattedPrimaryInterval,
  }));

  // ==================== Actions ====================

  /**
   * Fetch vendor brands from API (once — repeated calls are cheap via cache anyway)
   */
  const fetchVendorBrands = async (): Promise<void> => {
    if (vendorBrands.value.length) return;
    isVendorBrandLoading.value = true;

    try {
      const { execute } = fetchVendorBrandsRequest();
      const data = await execute();
      if (data) vendorBrands.value = data;
    }
    finally {
      isVendorBrandLoading.value = false;
    }
  };

  /**
   * Fetch vendor brand groups from API (once — repeated calls are cheap via cache anyway)
   */
  const fetchVendorBrandGroups = async (): Promise<void> => {
    if (vendorBrandGroups.value.length) return;
    isVendorBrandGroupLoading.value = true;

    try {
      const { execute } = fetchVendorBrandGroupsRequest();
      const data = await execute();
      if (data) vendorBrandGroups.value = data;
    }
    finally {
      isVendorBrandGroupLoading.value = false;
    }
  };

  /**
   * Set selected vendor brands — same "All" semantics as the seller filter
   */
  const setSelectedVendorBrands = (options: BrandFilterOption[]): void => {
    const hasAll = options.some(o => o.value === "all");
    const previousHadAll = selectedVendorBrands.value.some(o => o.value === "all");

    if (hasAll && !previousHadAll) {
      // User just selected "All" - reset to only All
      selectedVendorBrands.value = [ALL_VENDOR_BRANDS_OPTION];
    }
    else if (hasAll && options.length > 1) {
      // User selected something else while "All" was selected - remove "All"
      selectedVendorBrands.value = options.filter(o => o.value !== "all");
    }
    else if (options.length === 0) {
      // Nothing selected - default to "All"
      selectedVendorBrands.value = [ALL_VENDOR_BRANDS_OPTION];
    }
    else {
      selectedVendorBrands.value = options;
    }
  };

  /**
   * Set vendor brands from URL (by shortNames)
   */
  const setVendorBrandsFromShortNames = (shortNames: string[]): void => {
    if (shortNames.length === 0) {
      selectedVendorBrands.value = [ALL_VENDOR_BRANDS_OPTION];
      return;
    }

    const options: BrandFilterOption[] = vendorBrands.value
      .filter(b => shortNames.includes(b.BRAND_SHORT_NAME))
      .map(b => ({
        label: b.BRAND_NAME,
        value: b.VENDOR_BRAND_PK,
        shortName: b.BRAND_SHORT_NAME,
      }));

    selectedVendorBrands.value = options.length > 0 ? options : [ALL_VENDOR_BRANDS_OPTION];
  };

  /**
   * Search vendor products by ASINs
   * @param search - Comma/semicolon/newline separated string of ASINs
   */
  const searchVendorProducts = async (search: string): Promise<void> => {
    // Format search string: replace newlines and semicolons with commas
    const formattedSearch = search.replace(/[\r\n;]+/g, ",").trim();

    if (!formattedSearch) {
      // Clear search
      vendorProductSearch.value = [];
      searchVendorProductResult.value = [];
      return;
    }

    // Store search terms immediately (for URL sync and display)
    vendorProductSearch.value = formattedSearch.split(",").map(s => s.trim()).filter(Boolean);

    isSearchVendorProductLoading.value = true;

    try {
      const { execute } = searchVendorProductsRequest({ lazy: true });
      const data = await execute({
        data: {
          search: formattedSearch,
          brand: selectedVendorBrandIds.value,
          limit: VENDOR_PRODUCT_SEARCH_LIMIT,
        },
      });

      if (data && data.length > 0) {
        // Map results with labels (vendor products have no SKU — ASIN is the identifier)
        searchVendorProductResult.value = data.map(product => ({
          ...product,
          label_title: product.ASIN,
          label_name: product.PRODUCT_NAME,
        }));
      }
      else {
        searchVendorProductResult.value = [];
        warning(`No products found for this search term: "${formattedSearch}"`);
      }
    }
    finally {
      isSearchVendorProductLoading.value = false;
    }
  };

  /**
   * Clear vendor product search
   */
  const clearVendorProductSearch = (): void => {
    vendorProductSearch.value = [];
    searchVendorProductResult.value = [];
  };

  /**
   * Initialize vendor filters — load brands and restore selection from URL.
   * Idempotent: called on every entry into vendor mode, runs once per session.
   * @param query - URL query params to restore filters from
   */
  const initVendorFilters = async (query: Record<string, string | undefined>): Promise<void> => {
    if (isInitialized.value) return;

    await Promise.all([fetchVendorBrands(), fetchVendorBrandGroups()]);

    // Restore vendor brands from URL (by shortName)
    if (query.vendor_brands) {
      const shortNames = query.vendor_brands.split(",").filter(Boolean);
      setVendorBrandsFromShortNames(shortNames);
    }

    // Restore vendor products from URL (single request for all products)
    if (query.vendor_products) {
      await searchVendorProducts(query.vendor_products);
    }

    isInitialized.value = true;
  };

  /**
   * Reset selection to defaults (Reset to Default button) — keeps the loaded list
   */
  const resetSelection = (): void => {
    selectedVendorBrands.value = [ALL_VENDOR_BRANDS_OPTION];
    vendorProductSearch.value = [];
    searchVendorProductResult.value = [];
  };

  /**
   * Full reset — called on logout so a re-login as another user re-initializes
   */
  const reset = (): void => {
    vendorBrands.value = [];
    vendorBrandGroups.value = [];
    resetSelection();
    isInitialized.value = false;
  };

  return {
    // State
    isInitialized,
    vendorBrands,
    isVendorBrandLoading,
    vendorBrandGroups,
    isVendorBrandGroupLoading,
    selectedVendorBrands,
    vendorProductSearch,
    searchVendorProductResult,
    isSearchVendorProductLoading,

    // Getters
    isAllVendorBrandsSelected,
    selectedVendorBrandIds,
    selectedVendorProductIds,
    tableFilters,
    kpiRequestParams,
    tableRequestParams,

    // Actions
    fetchVendorBrands,
    fetchVendorBrandGroups,
    setSelectedVendorBrands,
    setVendorBrandsFromShortNames,
    searchVendorProducts,
    clearVendorProductSearch,
    initVendorFilters,
    resetSelection,
    reset,
  };
});
